import React, { useState, useEffect, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { enUS } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

const srLocale = {
  localize: {
    month: (n) => {
      const months = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
      return months[n];
    },
    day: (n) => {
      const days = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];
      return days[n];
    },
    ordinalNumber: (n) => n,
    era: (n) => (n === 0 ? 'Pre nove ere' : 'Nova era'),
    quarter: (n) => `Q${n}`,
    dayPeriod: (n) => (n === 0 ? 'AM' : 'PM'),
  },
  formatLong: {
    date: () => 'dd.MM.yyyy',
    time: () => 'HH:mm',
    dateTime: () => 'dd.MM.yyyy HH:mm',
  },
  options: {
    weekStartsOn: 1,
    firstWeekContainsDate: 1,
  },
  match: enUS?.match || {},
};

registerLocale('sr', srLocale);

const parseValue = (value) => {
  if (!value || typeof value !== 'string' || !value.trim()) return null;
  const parts = value.trim().split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(d.getTime()) ? null : d;
};

const DatePickerField = ({ value, onChange, inputClass, placeholder, maxDate, minDate, wrapperClassName, calendarClassName }) => {
  const pickerRef = useRef(null);
  const ignoreOpen = useRef(false);
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => parseValue(value));

  const closeCalendar = () => {
    ignoreOpen.current = true;
    setOpen(false);
    const inst = pickerRef.current;
    inst?.setOpen?.(false);
    inst?.input?.blur?.();
    window.setTimeout(() => {
      ignoreOpen.current = false;
    }, 250);
  };

  const openCalendar = () => {
    if (ignoreOpen.current) return;
    setOpen(true);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    closeCalendar();
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
    } else {
      onChange('');
    }
  };

  useEffect(() => {
    const next = parseValue(value);
    setSelectedDate((prev) => {
      if (!next) return null;
      if (prev && prev.getTime() === next.getTime()) return prev;
      return next;
    });
  }, [value]);

  return (
    <div className={`relative ${wrapperClassName || ''}`}>
      <DatePicker
        ref={pickerRef}
        selected={selectedDate}
        open={open}
        onInputClick={openCalendar}
        onClickOutside={closeCalendar}
        onChange={handleDateChange}
        shouldCloseOnSelect
        dateFormat="dd.MM.yyyy"
        placeholderText={placeholder || 'dd.mm.yyyy'}
        maxDate={maxDate}
        minDate={minDate}
        className={inputClass}
        locale="sr"
        calendarClassName={calendarClassName}
        weekStartsOn={1}
        showWeekNumbers={false}
        formatWeekDay={(dayName) => String(dayName || '').slice(0, 3)}
      />
    </div>
  );
};

export default DatePickerField;
